import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/presentation/stores/authStore';
import { useToast } from '@/presentation/composables/useToast';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: { name: 'login' },
  },
  {
    path: '/login',
    component: () => import('@/presentation/layouts/AuthLayout.vue'),
    children: [
      {
        path: '',
        name: 'login',
        component: () => import('@/presentation/views/LoginView.vue'),
        meta: { public: true },
      },
    ],
  },
  {
    path: '/auth',
    component: () => import('@/presentation/layouts/AuthLayout.vue'),
    children: [
      {
        path: 'forgot',
        name: 'auth-forgot',
        component: () => import('@/presentation/views/ForgotPasswordView.vue'),
        meta: { public: true },
      },
      {
        path: 'reset',
        name: 'auth-reset',
        component: () => import('@/presentation/views/ResetPasswordView.vue'),
        meta: { public: true },
      },
      {
        path: 'accept-invite',
        name: 'auth-accept-invite',
        component: () => import('@/presentation/views/AcceptInviteView.vue'),
        meta: { public: true },
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/presentation/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'hub',
        name: 'hub',
        component: () => import('@/presentation/views/HubView.vue'),
      },
      {
        path: 'admin/conta',
        name: 'admin-conta',
        component: () => import('@/presentation/views/conta/MinhaContaView.vue'),
        meta: { title: 'Minha conta', icon: 'mdi-account-circle-outline' },
      },
    ],
  },
  {
    path: '/admin/configuracoes',
    component: () => import('@/presentation/layouts/ConfiguracoesLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'admin-configuracoes',
        component: () => import('@/presentation/views/configuracoes/TenantFeaturesView.vue'),
        meta: { title: 'Funcionalidades', icon: 'mdi-cog-outline' },
      },
      {
        path: 'email-templates',
        name: 'admin-configuracoes-email-templates',
        component: () => import('@/presentation/views/configuracoes/EmailTemplatesView.vue'),
        meta: { title: 'Templates de e-mail', requiresAuthority: 'identity:emailtemplate:read' },
      },
      {
        path: 'integracoes',
        name: 'admin-configuracoes-integracoes',
        component: () => import('@/presentation/views/configuracoes/IntegracoesView.vue'),
        meta: { title: 'Integrações', requiresAuthority: 'org:integracao:read' },
      },
      {
        path: 'financeiro',
        name: 'admin-configuracoes-financeiro',
        component: () => import('@/presentation/views/configuracoes/ConfigFinanceiraView.vue'),
        meta: { title: 'Config financeira', requiresAuthority: 'org:config:read' },
      },
    ],
  },
  {
    path: '/admin/relatorios',
    component: () => import('@/presentation/layouts/RelatoriosLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'admin-relatorios-inadimplentes' } },
      {
        path: 'inadimplentes',
        name: 'admin-relatorios-inadimplentes',
        component: () => import('@/presentation/views/relatorios/InadimplentesView.vue'),
        meta: { title: 'Inadimplentes', requiresAuthority: 'commerce:cobranca:read' },
      },
      {
        path: 'adimplentes',
        name: 'admin-relatorios-adimplentes',
        component: () => import('@/presentation/views/relatorios/AdimplentesView.vue'),
        meta: { title: 'Adimplentes', requiresAuthority: 'commerce:cobranca:read' },
      },
      {
        path: 'pagamentos-por-polo',
        name: 'admin-relatorios-pagamentos-por-polo',
        component: () => import('@/presentation/views/relatorios/PagamentosPorPoloView.vue'),
        meta: { title: 'Pagamentos por polo', requiresAuthority: 'commerce:cobranca:read' },
      },
      {
        path: 'acesso-ava',
        name: 'admin-relatorios-acesso-ava',
        component: () => import('@/presentation/views/relatorios/AcessoAvaView.vue'),
        meta: { title: 'Acesso ao AVA', requiresAuthority: 'enrollment:aluno:read' },
      },
    ],
  },
  {
    path: '/admin/financeiro',
    component: () => import('@/presentation/layouts/FinanceiroLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'admin-financeiro',
        component: () => import('@/presentation/views/financeiro/FinanceiroDashboardView.vue'),
        meta: { title: 'Financeiro', icon: 'mdi-cash-multiple' },
      },
      {
        path: 'vendas',
        name: 'admin-financeiro-vendas',
        component: () => import('@/presentation/views/financeiro/VendasListView.vue'),
        meta: { title: 'Vendas', icon: 'mdi-cash-multiple', requiresAuthority: 'commerce:venda:read' },
      },
      {
        path: 'vendas/:id',
        name: 'admin-financeiro-venda-detalhe',
        component: () => import('@/presentation/views/financeiro/VendaDetailView.vue'),
        meta: { title: 'Detalhe da venda', requiresAuthority: 'commerce:venda:read' },
      },
      {
        path: 'parcelas',
        name: 'admin-financeiro-parcelas',
        component: () => import('@/presentation/views/financeiro/ParcelasListView.vue'),
        meta: { title: 'Parcelas', icon: 'mdi-receipt-text-outline', requiresAuthority: 'commerce:venda:read_all' },
      },
      {
        path: 'pagamentos',
        name: 'admin-financeiro-pagamentos',
        component: () => import('@/presentation/views/financeiro/PagamentosListView.vue'),
        meta: { title: 'Pagamentos', icon: 'mdi-cash-check', requiresAuthority: 'commerce:pagamento:read' },
      },
      {
        path: 'cobranca-envios',
        name: 'admin-financeiro-cobranca-envios',
        component: () => import('@/presentation/views/financeiro/CobrancaEnviosView.vue'),
        meta: { title: 'Envios da régua', icon: 'mdi-send-outline', requiresAuthority: 'commerce:cobranca:read' },
      },
      {
        path: 'comissoes-regras',
        name: 'admin-financeiro-comissoes-regras',
        component: () => import('@/presentation/views/financeiro/ComissaoRegrasView.vue'),
        meta: { title: 'Regras de comissão', icon: 'mdi-percent-outline', requiresAuthority: 'commerce:commission_rule:read' },
      },
      {
        path: 'comissoes',
        name: 'admin-financeiro-comissoes',
        component: () => import('@/presentation/views/financeiro/ComissoesView.vue'),
        meta: { title: 'Comissões', icon: 'mdi-percent', requiresAuthority: 'commerce:commission_entry:read' },
      },
      {
        path: 'inadimplencia',
        name: 'admin-financeiro-inadimplencia',
        component: () => import('@/presentation/views/financeiro/InadimplenciaView.vue'),
        meta: { title: 'Inadimplência', icon: 'mdi-alert-circle-outline', requiresAuthority: 'commerce:venda:read_all' },
      },
      {
        path: 'precos',
        name: 'admin-financeiro-precos',
        component: () => import('@/presentation/views/financeiro/PrecosView.vue'),
        meta: { title: 'Preços', icon: 'mdi-tag-outline', requiresAuthority: 'commerce:preco:read' },
      },
      {
        path: 'descontos',
        name: 'admin-financeiro-descontos',
        component: () => import('@/presentation/views/financeiro/DescontosView.vue'),
        meta: { title: 'Descontos', icon: 'mdi-ticket-percent-outline', requiresAuthority: 'commerce:desconto:read' },
      },
      {
        path: 'bolsas',
        name: 'admin-financeiro-bolsas',
        component: () => import('@/presentation/views/financeiro/BolsasView.vue'),
        meta: { title: 'Bolsas', icon: 'mdi-school-outline', requiresAuthority: 'commerce:desconto:read' },
      },
      {
        path: 'webhooks/subscriptions',
        name: 'admin-financeiro-webhook-subscriptions',
        component: () => import('@/presentation/views/financeiro/WebhookSubscriptionsView.vue'),
        meta: { title: 'Webhooks', icon: 'mdi-webhook', requiresAuthority: 'commerce:webhook_subscription:read' },
      },
      {
        path: 'webhooks/deliveries',
        name: 'admin-financeiro-webhook-deliveries',
        component: () => import('@/presentation/views/financeiro/WebhookDeliveriesView.vue'),
        meta: { title: 'Webhook deliveries', icon: 'mdi-truck-fast-outline', requiresAuthority: 'commerce:webhook_delivery:read' },
      },
      {
        path: 'metas-vendas',
        name: 'admin-financeiro-metas-vendas',
        component: () => import('@/presentation/views/financeiro/MetasVendasView.vue'),
        meta: { title: 'Metas de vendas', icon: 'mdi-target', requiresAuthority: 'commerce:meta_vendas:read' },
      },
      {
        path: 'alertas-venda',
        name: 'admin-financeiro-alertas-venda',
        component: () => import('@/presentation/views/financeiro/AlertasVendaView.vue'),
        meta: { title: 'Alertas de venda', icon: 'mdi-bell-alert-outline', requiresAuthority: 'commerce:alerta_venda:read' },
      },
      {
        path: 'cobranca-regua',
        name: 'admin-financeiro-cobranca-regua',
        component: () => import('@/presentation/views/financeiro/CobrancaReguasView.vue'),
        meta: { title: 'Régua de cobrança', icon: 'mdi-calendar-clock-outline', requiresAuthority: 'commerce:cobranca:read' },
      },
      {
        path: 'gateway',
        name: 'admin-financeiro-gateway',
        component: () => import('@/presentation/views/financeiro/GatewayConfigView.vue'),
        meta: { title: 'Gateway de pagamento', icon: 'mdi-flash', requiresAuthority: 'commerce:gateway:manage' },
      },
      {
        path: 'gateway-credenciais',
        name: 'admin-financeiro-gateway-credenciais',
        component: () => import('@/presentation/views/financeiro/GatewayCredenciaisView.vue'),
        meta: { title: 'Credenciais de gateway', icon: 'mdi-key-chain-variant', requiresAuthority: 'commerce:gateway:read' },
      },
      {
        path: 'renegociacoes',
        name: 'admin-financeiro-renegociacoes',
        component: () => import('@/presentation/views/financeiro/RenegociacoesAdminView.vue'),
        meta: { title: 'Renegociações', icon: 'mdi-handshake-outline', requiresAuthority: 'commerce:renegociacao:read' },
      },
      {
        path: 'estornos',
        name: 'admin-financeiro-estornos',
        component: () => import('@/presentation/views/financeiro/EstornosAdminView.vue'),
        meta: { title: 'Estornos', icon: 'mdi-arrow-u-left-top', requiresAuthority: 'commerce:estorno:read' },
      },
    ],
  },
  {
    path: '/admin/comercial',
    component: () => import('@/presentation/layouts/ComercialLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'admin-comercial',
        component: () => import('@/presentation/views/comercial/ComercialLeadsView.vue'),
        meta: { title: 'Comercial / CRM', icon: 'mdi-chart-line', requiresAuthority: 'commerce:lead:read_self' },
      },
      {
        path: 'leads/:id',
        name: 'admin-comercial-lead-detalhe',
        component: () => import('@/presentation/views/comercial/LeadDetalheView.vue'),
        meta: { title: 'Lead', icon: 'mdi-account-outline', requiresAuthority: 'commerce:lead:read_self' },
      },
      {
        path: 'kanban',
        name: 'admin-comercial-kanban',
        component: () => import('@/presentation/views/comercial/ComercialLeadsKanbanView.vue'),
        meta: { title: 'Kanban', icon: 'mdi-view-column-outline', requiresAuthority: 'commerce:lead:read_self' },
      },
      {
        path: 'oportunidades',
        name: 'admin-comercial-oportunidades',
        component: () => import('@/presentation/views/comercial/OpportunitiesView.vue'),
        meta: { title: 'Oportunidades', icon: 'mdi-target', requiresAuthority: 'commerce:opportunity:read' },
      },
      {
        path: 'pipelines',
        name: 'admin-comercial-pipelines',
        component: () => import('@/presentation/views/comercial/PipelinesView.vue'),
        meta: { title: 'Pipelines', icon: 'mdi-sitemap-outline', requiresAuthority: 'commerce:pipeline:read' },
      },
      {
        path: 'motivos-perda',
        name: 'admin-comercial-motivos-perda',
        component: () => import('@/presentation/views/comercial/LossReasonsView.vue'),
        meta: { title: 'Motivos de perda', icon: 'mdi-thumb-down-outline', requiresAuthority: 'commerce:lossreason:manage' },
      },
      {
        path: 'cadencias',
        name: 'admin-comercial-cadencias',
        component: () => import('@/presentation/views/comercial/CadenciasView.vue'),
        meta: { title: 'Cadências', icon: 'mdi-calendar-sync-outline', requiresAuthority: 'commerce:cadencia:read' },
      },
      {
        path: 'atividades',
        name: 'admin-comercial-atividades',
        component: () => import('@/presentation/views/comercial/AtividadesView.vue'),
        meta: { title: 'Atividades', icon: 'mdi-timeline-text-outline', requiresAuthority: 'commerce:activity:read' },
      },
      {
        path: 'descontos-extras',
        name: 'admin-comercial-descontos-extras',
        component: () => import('@/presentation/views/comercial/SolicitacoesDescontoExtraView.vue'),
        meta: { title: 'Descontos extras', icon: 'mdi-sale-outline', requiresAuthority: 'commerce:solicitacao_extra:read' },
      },
      {
        path: 'agenciadores',
        name: 'admin-comercial-agenciadores',
        component: () => import('@/presentation/views/comercial/AgenciadoresView.vue'),
        meta: { title: 'Agenciadores', icon: 'mdi-account-cash-outline', requiresAuthority: 'org:agenciador:read' },
      },
      {
        path: 'grupos-venda',
        name: 'admin-comercial-grupos-venda',
        component: () => import('@/presentation/views/comercial/GruposVendaView.vue'),
        meta: { title: 'Grupos de venda', requiresAuthority: 'commerce:grupovenda:read' },
      },
      {
        path: 'links-captacao',
        name: 'admin-comercial-links-captacao',
        component: () => import('@/presentation/views/comercial/LinksCaptacaoView.vue'),
        meta: { title: 'Links de captação', requiresAuthority: 'commerce:linkcaptacao:read' },
      },
      {
        path: 'processos-seletivos',
        name: 'admin-comercial-processos-seletivos',
        component: () => import('@/presentation/views/comercial/ProcessosSeletivosView.vue'),
        meta: { title: 'Processos seletivos', requiresAuthority: 'commerce:processoseletivo:read' },
      },
      {
        path: 'captacao',
        name: 'admin-comercial-captacao',
        component: () => import('@/presentation/views/comercial/CaptacaoSetupView.vue'),
        meta: { title: 'Captação pública', icon: 'mdi-link-variant', requiresAuthority: 'commerce:lead:read_self' },
      },
      {
        path: 'dashboard',
        name: 'admin-comercial-dashboard',
        component: () => import('@/presentation/views/comercial/ComercialDashboardView.vue'),
        meta: { title: 'Dashboard CRM', icon: 'mdi-view-dashboard-outline', requiresAuthority: 'commerce:lead:read_self' },
      },
    ],
  },
  {
    path: '/admin/rh',
    component: () => import('@/presentation/layouts/RhLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'admin-rh-docentes' } },
      {
        path: 'docentes',
        name: 'admin-rh-docentes',
        component: () => import('@/presentation/views/rh/DocentesListView.vue'),
        meta: { title: 'Docentes', icon: 'mdi-account-school-outline', requiresAuthority: 'faculty:docente:read' },
      },
      {
        path: 'docentes/:id',
        name: 'admin-rh-docente-detail',
        component: () => import('@/presentation/views/rh/DocenteDetailView.vue'),
        meta: { title: 'Detalhe do docente', requiresAuthority: 'faculty:docente:read' },
      },
    ],
  },
  {
    path: '/admin/acessos',
    component: () => import('@/presentation/layouts/AcessosLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'admin-acessos-usuarios' } },
      {
        path: 'usuarios',
        name: 'admin-acessos-usuarios',
        component: () => import('@/presentation/views/acessos/UsuariosView.vue'),
        meta: { title: 'Usuários', icon: 'mdi-account-multiple-outline', requiresAuthority: 'identity:user:read.tenant' },
      },
      {
        path: 'papeis',
        name: 'admin-acessos-papeis',
        component: () => import('@/presentation/views/acessos/PapeisView.vue'),
        meta: { title: 'Papéis', icon: 'mdi-shield-account-outline', requiresAuthority: 'identity:role:read' },
      },
      {
        path: 'convites',
        name: 'admin-acessos-convites',
        component: () => import('@/presentation/views/acessos/ConvitesView.vue'),
        meta: { title: 'Convites', icon: 'mdi-email-outline', requiresAuthority: 'identity:invitation:read.tenant' },
      },
    ],
  },
  {
    path: '/admin/organizacao',
    component: () => import('@/presentation/layouts/OrganizacaoLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'admin-organizacao-polos' } },
      {
        path: 'polos',
        name: 'admin-organizacao-polos',
        component: () => import('@/presentation/views/organization/PolosView.vue'),
        meta: { title: 'Polos', icon: 'mdi-office-building-marker-outline', requiresAuthority: 'org:polo:read' },
      },
      {
        path: 'setores',
        name: 'admin-organizacao-setores',
        component: () => import('@/presentation/views/organization/SectoresView.vue'),
        meta: { title: 'Setores', icon: 'mdi-sitemap-outline', requiresAuthority: 'org:sector:read' },
      },
      {
        path: 'mantenedoras',
        name: 'admin-organizacao-mantenedoras',
        component: () => import('@/presentation/views/organization/MantenedorasView.vue'),
        meta: { title: 'Mantenedoras', icon: 'mdi-domain', requiresAuthority: 'org:mantenedora:read' },
      },
      {
        path: 'onboarding',
        name: 'admin-organizacao-onboarding',
        component: () => import('@/presentation/views/organization/OnboardTenantView.vue'),
        meta: { title: 'Provisionar IES', icon: 'mdi-rocket-launch-outline', requiresAuthority: 'org:tenant:create' },
      },
    ],
  },
  {
    path: '/admin/material-didatico',
    component: () => import('@/presentation/layouts/MaterialDidaticoLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'material-conteudo-externo' } },
      {
        path: 'unidades',
        name: 'material-unidades',
        component: () => import('@/presentation/views/pedagogico/UnitsCatalogView.vue'),
        meta: { title: 'Unidades', icon: 'mdi-bookshelf', requiresAuthority: 'content:unidade:read' },
      },
      {
        path: 'conteudo-externo',
        name: 'material-conteudo-externo',
        component: () => import('@/presentation/views/pedagogico/ConteudoExternoView.vue'),
        meta: { title: 'Conteúdo externo', icon: 'mdi-bookshelf', requiresAuthority: 'content:external:read' },
      },
      {
        path: 'ebooks',
        name: 'material-ebooks',
        component: () => import('@/presentation/views/pedagogico/EbooksView.vue'),
        meta: { title: 'Ebooks', icon: 'mdi-book-open-page-variant-outline', requiresAuthority: 'content:ebook:read' },
      },
      {
        path: 'videoaulas',
        name: 'material-videoaulas',
        component: () => import('@/presentation/views/pedagogico/VideoaulasView.vue'),
        meta: { title: 'Videoaulas', icon: 'mdi-play-box-outline', requiresAuthority: 'content:videoaula:read' },
      },
      {
        path: 'ambientacoes',
        name: 'material-ambientacoes',
        component: () => import('@/presentation/views/pedagogico/AmbientacoesView.vue'),
        meta: { title: 'Ambientações', icon: 'mdi-compass-outline', requiresAuthority: 'content:ambientacao:read' },
      },
      {
        path: 'outros-conteudos',
        name: 'material-outros-conteudos',
        component: () => import('@/presentation/views/pedagogico/OutrosConteudosView.vue'),
        meta: { title: 'Outros conteúdos', icon: 'mdi-shape-plus-outline', requiresAuthority: 'content:outro:read' },
      },
      {
        path: 'conteudo-aura',
        name: 'material-conteudo-aura',
        component: () => import('@/presentation/views/pedagogico/ConteudoAuraView.vue'),
        meta: { title: 'Conteúdo Aura', icon: 'mdi-cloud-braces', requiresAuthority: 'content:external:read' },
      },
      {
        // Criação de avaliação vive no Material Didático (paridade referência).
        path: 'avaliacoes',
        name: 'material-avaliacoes',
        component: () => import('@/presentation/views/pedagogico/AvaliacoesListView.vue'),
        meta: { title: 'Avaliações', icon: 'mdi-clipboard-text-outline', requiresAuthority: 'assessment:read' },
      },
      {
        path: 'avaliacoes/nova',
        name: 'material-avaliacao-nova',
        component: () => import('@/presentation/views/pedagogico/AvaliacaoBuilderView.vue'),
        meta: { title: 'Nova avaliação', requiresAuthority: 'assessment:manage' },
      },
      {
        path: 'avaliacoes/:id/editar',
        name: 'material-avaliacao-editar',
        component: () => import('@/presentation/views/pedagogico/AvaliacaoBuilderView.vue'),
        meta: { title: 'Editar avaliação', requiresAuthority: 'assessment:manage' },
      },
      {
        path: 'provedores-conteudo',
        name: 'material-provedores-conteudo',
        component: () => import('@/presentation/views/pedagogico/ProvedoresConteudoView.vue'),
        meta: { title: 'Provedores de conteúdo', icon: 'mdi-cloud-outline', requiresAuthority: 'content:provider:read' },
      },
    ],
  },
  {
    path: '/admin/pedagogico',
    component: () => import('@/presentation/layouts/PedagogicoLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'pedagogico-dashboard' } },
      {
        path: 'dashboard',
        name: 'pedagogico-dashboard',
        component: () =>
          import('@/presentation/views/pedagogico/PedagogicoDashboardView.vue'),
        meta: { title: 'Dashboard Pedagógico' },
      },
      {
        path: 'cursos',
        name: 'pedagogico-cursos',
        component: () =>
          import('@/presentation/views/pedagogico/CoursesListView.vue'),
        meta: { title: 'Cursos', requiresAuthority: 'catalog:course:read' },
      },
      {
        path: 'cursos/novo',
        name: 'pedagogico-curso-novo',
        component: () =>
          import('@/presentation/views/pedagogico/CourseCreateView.vue'),
        meta: { title: 'Novo curso', requiresAuthority: 'catalog:course:create' },
      },
      {
        path: 'cursos/:id',
        name: 'pedagogico-curso-detalhe',
        component: () =>
          import('@/presentation/views/pedagogico/CourseDetailView.vue'),
        meta: { title: 'Detalhe do curso', requiresAuthority: 'catalog:course:read' },
      },
      {
        path: 'matrizes',
        name: 'pedagogico-matrizes',
        component: () =>
          import('@/presentation/views/pedagogico/MatrizesListView.vue'),
        meta: { title: 'Matrizes curriculares', requiresAuthority: 'catalog:matrix:read' },
      },
      {
        path: 'matrizes/nova',
        name: 'pedagogico-matriz-nova',
        component: () =>
          import('@/presentation/views/pedagogico/MatrizCreateView.vue'),
        meta: { title: 'Nova matriz', requiresAuthority: 'catalog:matrix:create' },
      },
      {
        path: 'matrizes/:id',
        name: 'pedagogico-matriz-detalhe',
        component: () =>
          import('@/presentation/views/pedagogico/MatrizDetailView.vue'),
        meta: { title: 'Detalhe da matriz', requiresAuthority: 'catalog:matrix:read' },
      },
      {
        path: 'disciplinas',
        name: 'pedagogico-disciplinas',
        component: () =>
          import('@/presentation/views/pedagogico/DisciplinesListView.vue'),
        meta: { title: 'Disciplinas', requiresAuthority: 'catalog:discipline:read' },
      },
      {
        path: 'disciplinas/nova',
        name: 'pedagogico-disciplina-nova',
        component: () =>
          import('@/presentation/views/pedagogico/DisciplineCreateView.vue'),
        meta: { title: 'Nova disciplina', requiresAuthority: 'catalog:discipline:create' },
      },
      {
        path: 'disciplinas/:id',
        name: 'pedagogico-disciplina-detalhe',
        component: () =>
          import('@/presentation/views/pedagogico/DisciplineDetailView.vue'),
        meta: { title: 'Detalhe da disciplina', requiresAuthority: 'catalog:discipline:read' },
      },
      {
        path: 'questoes',
        name: 'pedagogico-questoes',
        component: () =>
          import('@/presentation/views/pedagogico/BancoQuestoesView.vue'),
        meta: {
          title: 'Banco de questões',
          icon: 'mdi-help-circle-outline',
          requiresAuthority: 'assessment:read',
        },
      },
      {
        path: 'notas',
        name: 'pedagogico-notas',
        component: () =>
          import('@/presentation/views/pedagogico/NotasView.vue'),
        meta: {
          title: 'Notas',
          icon: 'mdi-clipboard-check-outline',
          requiresAuthority: 'assessment:read',
        },
      },
      {
        path: 'tentativas',
        name: 'pedagogico-tentativas',
        component: () =>
          import('@/presentation/views/pedagogico/TentativasView.vue'),
        meta: {
          title: 'Tentativas',
          icon: 'mdi-progress-clock',
          requiresAuthority: 'assessment:read',
        },
      },
      {
        path: 'metodos-avaliativos',
        name: 'pedagogico-metodos-avaliativos',
        component: () =>
          import('@/presentation/views/pedagogico/MetodosAvaliativosView.vue'),
        meta: {
          title: 'Métodos avaliativos',
          icon: 'mdi-calculator-variant-outline',
          requiresAuthority: 'assessment:read',
        },
      },
      // ─── Atividades & Fórum (H.2) ──────────────────────────────────────
      {
        path: 'atividades',
        name: 'pedagogico-atividades',
        component: () => import('@/presentation/views/pedagogico/AtividadesView.vue'),
        meta: { title: 'Atividades', requiresAuthority: 'assignment:read' },
      },
      {
        path: 'forum',
        name: 'pedagogico-forum',
        component: () => import('@/presentation/views/pedagogico/ForumView.vue'),
        meta: { title: 'Fórum', requiresAuthority: 'comunicacao:forum:read' },
      },
      // ─── Tutoria (H.3) ─────────────────────────────────────────────────
      {
        path: 'tutores',
        name: 'pedagogico-tutores',
        component: () => import('@/presentation/views/pedagogico/TutoresView.vue'),
        meta: { title: 'Tutores', requiresAuthority: 'tutoria:tutor:read' },
      },
      {
        path: 'tutoria/assuntos',
        name: 'pedagogico-tutoria-assuntos',
        component: () => import('@/presentation/views/pedagogico/AssuntosView.vue'),
        meta: { title: 'Assuntos de tutoria', requiresAuthority: 'tutoria:assunto:read' },
      },
      {
        path: 'tutoria/solicitacoes',
        name: 'pedagogico-tutoria-solicitacoes',
        component: () => import('@/presentation/views/pedagogico/SolicitacoesTutoriaView.vue'),
        meta: { title: 'Solicitações de tutoria', requiresAuthority: 'tutoria:solicitacao:read' },
      },
      // ─── Comunicação (H.4) ─────────────────────────────────────────────
      {
        path: 'aulas-ao-vivo',
        name: 'pedagogico-aulas-ao-vivo',
        component: () => import('@/presentation/views/pedagogico/AulasView.vue'),
        meta: { title: 'Aulas ao vivo', requiresAuthority: 'comunicacao:aula:read' },
      },
      {
        path: 'materiais-apoio',
        name: 'pedagogico-materiais-apoio',
        component: () => import('@/presentation/views/pedagogico/MateriaisView.vue'),
        meta: { title: 'Materiais de apoio', requiresAuthority: 'comunicacao:material:read' },
      },
      {
        path: 'manuais',
        name: 'pedagogico-manuais',
        component: () => import('@/presentation/views/pedagogico/ManuaisView.vue'),
        meta: { title: 'Manuais', requiresAuthority: 'comunicacao:manual:read' },
      },
      {
        path: 'mensagens',
        name: 'pedagogico-mensagens',
        component: () => import('@/presentation/views/pedagogico/MensagensView.vue'),
        meta: { title: 'Mensagens', requiresAuthority: 'comunicacao:mensagem:manage' },
      },
      // ─── Config do catálogo (H.1) ──────────────────────────────────────
      {
        path: 'niveis-ensino',
        name: 'pedagogico-niveis-ensino',
        component: () => import('@/presentation/views/pedagogico/EducationLevelsView.vue'),
        meta: { title: 'Níveis de ensino', requiresAuthority: 'catalog:educationlevel:read' },
      },
      {
        path: 'areas-conhecimento',
        name: 'pedagogico-areas-conhecimento',
        component: () => import('@/presentation/views/pedagogico/KnowledgeAreasView.vue'),
        meta: { title: 'Áreas do conhecimento', requiresAuthority: 'catalog:area:read' },
      },
      {
        path: 'titulacoes',
        name: 'pedagogico-titulacoes',
        component: () => import('@/presentation/views/pedagogico/TitulacoesView.vue'),
        meta: { title: 'Titulações', requiresAuthority: 'catalog:titulacao:read' },
      },
      {
        path: 'regras-rematricula',
        name: 'pedagogico-regras-rematricula',
        component: () => import('@/presentation/views/pedagogico/RegrasRematriculaView.vue'),
        meta: { title: 'Regras de rematrícula', requiresAuthority: 'catalog:rematricula:read' },
      },
    ],
  },
  {
    path: '/admin/academico',
    component: () => import('@/presentation/layouts/AcademicoLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'academico-dashboard' } },
      {
        path: 'dashboard',
        name: 'academico-dashboard',
        component: () =>
          import('@/presentation/views/academico/AcademicoDashboardView.vue'),
        meta: { title: 'Dashboard Acadêmico' },
      },
      {
        path: 'periodos-letivos',
        name: 'academico-periodos-letivos',
        component: () =>
          import('@/presentation/views/academico/PeriodosLetivosView.vue'),
        meta: { title: 'Períodos letivos', icon: 'mdi-calendar-range-outline', requiresAuthority: 'enrollment:periodo_letivo:read' },
      },
      {
        path: 'calendario',
        name: 'academico-calendario',
        component: () =>
          import('@/presentation/views/academico/CalendarioAcademicoView.vue'),
        meta: { title: 'Calendário acadêmico', icon: 'mdi-calendar-star-outline', requiresAuthority: 'enrollment:calendario:read' },
      },
      {
        path: 'alunos',
        name: 'academico-alunos',
        component: () =>
          import('@/presentation/views/academico/AlunosListView.vue'),
        meta: { title: 'Alunos' },
      },
      {
        path: 'alunos/novo',
        name: 'academico-aluno-novo',
        component: () =>
          import('@/presentation/views/academico/AlunoCreateView.vue'),
        meta: { title: 'Cadastrar aluno', requiresAuthority: 'enrollment:aluno:create' },
      },
      {
        path: 'alunos/:id',
        name: 'academico-aluno-detalhe',
        component: () =>
          import('@/presentation/views/academico/AlunoDetailView.vue'),
        meta: { title: 'Detalhe do aluno' },
      },
      {
        path: 'matriculas',
        name: 'academico-matriculas',
        component: () =>
          import('@/presentation/views/academico/MatriculasListView.vue'),
        meta: { title: 'Matrículas', requiresAuthority: 'enrollment:aluno:read' },
      },
      {
        path: 'matriculas/:id',
        name: 'academico-matricula-detalhe',
        component: () =>
          import('@/presentation/views/academico/MatriculaDetailView.vue'),
        meta: { title: 'Detalhe da matrícula', requiresAuthority: 'enrollment:aluno:read' },
      },
      {
        path: 'cohorts',
        name: 'academico-cohorts',
        component: () =>
          import('@/presentation/views/academico/CohortsListView.vue'),
        meta: { title: 'Turmas' },
      },
      {
        path: 'cohorts/nova',
        name: 'academico-cohort-nova',
        component: () =>
          import('@/presentation/views/academico/CohortCreateView.vue'),
        meta: { title: 'Nova turma', requiresAuthority: 'enrollment:cohort:create' },
      },
      {
        path: 'cohorts/:id',
        name: 'academico-cohort-detalhe',
        component: () =>
          import('@/presentation/views/academico/CohortDetailView.vue'),
        meta: { title: 'Detalhe da turma' },
      },
      // Frequências/Contratos/Parcelas: acessíveis como abas da MatriculaDetailView
      // (sem rota/placeholder dedicado no nível do módulo).
      // Requerimentos e CPA reais vivem no slice acadadmin (/admin/acadadmin/*).
      // ─── S3 Acadêmico (gaps SA→AVA) ───────────────────────────────────────
      {
        path: '/admin/acadadmin/noticias',
        name: 'admin-acadadmin-noticias',
        component: () => import('@/presentation/views/acadadmin/NoticiasView.vue'),
        meta: { title: 'Comunicados', requiresAuthority: 'comunicacao:noticia:read' },
      },
      {
        path: '/admin/acadadmin/livros-registros',
        name: 'admin-acadadmin-livros-registros',
        component: () => import('@/presentation/views/acadadmin/LivrosRegistroView.vue'),
        meta: { title: 'Livro de registros', requiresAuthority: 'acadadmin:livro:read' },
      },
      {
        path: '/admin/academico/notas-multidisciplinar',
        name: 'admin-academico-notas-multidisciplinar',
        component: () => import('@/presentation/views/academico/NotasMultidisciplinarView.vue'),
        meta: { title: 'Notas multidisciplinar', requiresAuthority: 'enrollment:aluno:read' },
      },
      // ─── Slice acadadmin (G.5.1..G.5.12) — paths absolutos preservando URLs externas ───
      {
        path: '/admin/acadadmin/horas-complementares',
        name: 'admin-acadadmin-horas-complementares',
        component: () => import('@/presentation/views/acadadmin/HorasComplementaresView.vue'),
        meta: { title: 'Horas complementares', icon: 'mdi-clock-outline', requiresAuthority: 'acadadmin:horas:read' },
      },
      {
        path: '/admin/acadadmin/horas-fila',
        name: 'admin-acadadmin-horas-fila',
        component: () => import('@/presentation/views/acadadmin/HorasFilaView.vue'),
        meta: { title: 'Fila de horas', icon: 'mdi-clock-check-outline', requiresAuthority: 'acadadmin:horas:read' },
      },
      {
        path: '/admin/acadadmin/documento-tipos',
        name: 'admin-acadadmin-documento-tipos',
        component: () => import('@/presentation/views/acadadmin/DocumentoTiposView.vue'),
        meta: { title: 'Tipos de documento', icon: 'mdi-file-document-outline', requiresAuthority: 'acadadmin:documento_tipo:read' },
      },
      {
        path: '/admin/acadadmin/documentos-fila',
        name: 'admin-acadadmin-documentos-fila',
        component: () => import('@/presentation/views/acadadmin/DocumentosFilaView.vue'),
        meta: { title: 'Fila de validação', icon: 'mdi-check-decagram', requiresAuthority: 'acadadmin:documento:validate' },
      },
      {
        path: '/admin/acadadmin/documento-modelos',
        name: 'admin-acadadmin-documento-modelos',
        component: () => import('@/presentation/views/acadadmin/DocumentoModelosView.vue'),
        meta: { title: 'Modelos de documento', icon: 'mdi-file-document-multiple-outline', requiresAuthority: 'acadadmin:modelo:read' },
      },
      {
        path: '/admin/acadadmin/documentos-emitidos',
        name: 'admin-acadadmin-documentos-emitidos',
        component: () => import('@/presentation/views/acadadmin/DocumentosEmitidosAlunoView.vue'),
        meta: { title: 'Documentos emitidos', icon: 'mdi-file-document-arrow-right-outline', requiresAuthority: 'acadadmin:emissao:read' },
      },
      {
        path: '/admin/acadadmin/protocolo-cadastros',
        name: 'admin-acadadmin-protocolo-cadastros',
        component: () => import('@/presentation/views/acadadmin/ProtocoloCadastrosView.vue'),
        meta: { title: 'Cadastros de Protocolo', icon: 'mdi-folder-cog-outline', requiresAuthority: 'acadadmin:protocolo_setor:read' },
      },
      {
        path: '/admin/acadadmin/protocolos-fila',
        name: 'admin-acadadmin-protocolos-fila',
        component: () => import('@/presentation/views/acadadmin/ProtocolosFilaView.vue'),
        meta: { title: 'Fila de protocolos', icon: 'mdi-folder-multiple-outline', requiresAuthority: 'acadadmin:protocolo:atender' },
      },
      {
        path: '/admin/acadadmin/historico-escolar',
        name: 'admin-acadadmin-historico-escolar',
        component: () => import('@/presentation/views/acadadmin/HistoricoEscolarView.vue'),
        meta: { title: 'Histórico Escolar', icon: 'mdi-school-outline', requiresAuthority: 'assessment:historico:read' },
      },
      {
        path: '/admin/acadadmin/certificados',
        name: 'admin-acadadmin-certificados',
        component: () => import('@/presentation/views/acadadmin/CertificadosView.vue'),
        meta: { title: 'Certificados', icon: 'mdi-certificate-outline', requiresAuthority: 'acadadmin:certificado:read' },
      },
      {
        path: '/admin/acadadmin/agendamento-recursos',
        name: 'admin-acadadmin-agendamento-recursos',
        component: () => import('@/presentation/views/acadadmin/AgendamentoRecursosView.vue'),
        meta: { title: 'Recursos de agendamento', icon: 'mdi-door-open', requiresAuthority: 'acadadmin:agendamento_recurso:read' },
      },
      {
        path: '/admin/acadadmin/calendario',
        name: 'admin-acadadmin-calendario',
        component: () => import('@/presentation/views/acadadmin/CalendarioView.vue'),
        meta: { title: 'Calendário', icon: 'mdi-calendar-month-outline', requiresAuthority: 'acadadmin:agendamento:read' },
      },
      {
        path: '/admin/acadadmin/solicitacoes-conclusao',
        name: 'admin-acadadmin-solicitacoes-conclusao',
        component: () => import('@/presentation/views/acadadmin/SolicitacoesConclusaoView.vue'),
        meta: { title: 'Solicitações de conclusão', icon: 'mdi-school-outline', requiresAuthority: 'acadadmin:solicitacao_conclusao:read' },
      },
      {
        path: '/admin/acadadmin/solicitacoes-conclusao/:id',
        name: 'admin-acadadmin-solicitacao-conclusao-detalhe',
        component: () => import('@/presentation/views/acadadmin/SolicitacaoConclusaoDetailView.vue'),
        meta: { title: 'Detalhe da solicitação', requiresAuthority: 'acadadmin:solicitacao_conclusao:read' },
      },
      {
        path: '/admin/acadadmin/formularios',
        name: 'admin-acadadmin-formularios',
        component: () => import('@/presentation/views/acadadmin/FormulariosView.vue'),
        meta: { title: 'Formulários', icon: 'mdi-clipboard-list-outline', requiresAuthority: 'acadadmin:formulario:read' },
      },
      {
        path: '/admin/acadadmin/formularios/:id',
        name: 'admin-acadadmin-formulario-detalhe',
        component: () => import('@/presentation/views/acadadmin/FormularioDetailView.vue'),
        meta: { title: 'Detalhe do formulário', requiresAuthority: 'acadadmin:formulario:read' },
      },
      {
        path: '/admin/acadadmin/cpa',
        name: 'admin-acadadmin-cpa',
        component: () => import('@/presentation/views/acadadmin/CpaCiclosView.vue'),
        meta: { title: 'CPA', icon: 'mdi-poll', requiresAuthority: 'acadadmin:cpa:read' },
      },
      {
        path: '/admin/acadadmin/cpa/:id',
        name: 'admin-acadadmin-cpa-detalhe',
        component: () => import('@/presentation/views/acadadmin/CpaCicloDetailView.vue'),
        meta: { title: 'Detalhe do ciclo CPA', requiresAuthority: 'acadadmin:cpa:read' },
      },
      {
        path: '/admin/acadadmin/requerimento-tipos',
        name: 'admin-acadadmin-requerimento-tipos',
        component: () => import('@/presentation/views/acadadmin/RequerimentoTiposView.vue'),
        meta: { title: 'Tipos de requerimento', icon: 'mdi-clipboard-edit-outline', requiresAuthority: 'acadadmin:requerimento_tipo:read' },
      },
      {
        path: '/admin/acadadmin/requerimentos',
        name: 'admin-acadadmin-requerimentos',
        component: () => import('@/presentation/views/acadadmin/RequerimentosView.vue'),
        meta: { title: 'Requerimentos', icon: 'mdi-clipboard-text-outline', requiresAuthority: 'acadadmin:requerimento:triage' },
      },
      {
        path: '/admin/acadadmin/requerimentos/fila',
        name: 'admin-acadadmin-requerimentos-fila',
        component: () => import('@/presentation/views/acadadmin/RequerimentoFilaView.vue'),
        meta: { title: 'Fila de requerimentos', icon: 'mdi-clipboard-check-outline', requiresAuthority: 'acadadmin:requerimento:triage' },
      },
      {
        path: '/admin/acadadmin/requerimentos/:id',
        name: 'admin-acadadmin-requerimento-detalhe',
        component: () => import('@/presentation/views/acadadmin/RequerimentoDetailView.vue'),
        meta: { title: 'Detalhe do requerimento', requiresAuthority: 'acadadmin:requerimento:read' },
      },
      {
        path: '/admin/acadadmin/lgpd-solicitacoes',
        name: 'admin-acadadmin-lgpd-solicitacoes',
        component: () => import('@/presentation/views/acadadmin/LgpdSolicitacoesView.vue'),
        meta: { title: 'LGPD — Solicitações', icon: 'mdi-shield-account-outline', requiresAuthority: 'lgpd:solicitacao:read' },
      },
      {
        path: '/admin/acadadmin/contrato-tipos',
        name: 'admin-acadadmin-contrato-tipos',
        component: () => import('@/presentation/views/acadadmin/ContratoTiposView.vue'),
        meta: { title: 'Tipos de contrato', icon: 'mdi-file-sign', requiresAuthority: 'acadadmin:contrato_tipo:read' },
      },
      {
        path: '/admin/acadadmin/contrato-templates',
        name: 'admin-acadadmin-contrato-templates',
        component: () => import('@/presentation/views/acadadmin/ContratoTemplatesView.vue'),
        meta: { title: 'Templates de contrato', icon: 'mdi-file-document-edit-outline', requiresAuthority: 'acadadmin:contrato_template:read' },
      },
      {
        path: '/admin/acadadmin/certificado-templates',
        name: 'admin-acadadmin-certificado-templates',
        component: () => import('@/presentation/views/acadadmin/CertificadoTemplatesView.vue'),
        meta: { title: 'Templates de certificado', icon: 'mdi-certificate-outline', requiresAuthority: 'acadadmin:certificado_template:read' },
      },
      {
        path: '/admin/acadadmin/ofertas',
        name: 'admin-acadadmin-ofertas',
        component: () => import('@/presentation/views/acadadmin/OfertasView.vue'),
        meta: { title: 'Ofertas (turmas)', icon: 'mdi-google-classroom', requiresAuthority: 'enrollment:oferta:read' },
      },
      {
        path: '/admin/acadadmin/ofertas/:id/diario',
        name: 'admin-acadadmin-oferta-diario',
        component: () => import('@/presentation/views/acadadmin/OfertaDiarioView.vue'),
        meta: { title: 'Diário de classe', requiresAuthority: 'enrollment:diario:read' },
      },
      {
        path: '/admin/acadadmin/protocolos/:id',
        name: 'admin-acadadmin-protocolo-detalhe',
        component: () => import('@/presentation/views/acadadmin/ProtocoloDetailView.vue'),
        meta: { title: 'Detalhe do protocolo', requiresAuthority: 'acadadmin:protocolo:read' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'login' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.isInitialized) {
    await auth.bootstrap();
  }
  if (to.meta['requiresAuth'] && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (to.meta['public'] && auth.isAuthenticated && to.name === 'login') {
    return { name: 'hub' };
  }
  const requiredAuthority = to.meta['requiresAuthority'];
  if (
    typeof requiredAuthority === 'string' &&
    auth.isAuthenticated &&
    !auth.hasAuthority(requiredAuthority) &&
    !auth.user?.roles?.includes('SUPERADMIN') &&
    !auth.user?.roles?.includes('TENANT_ADMIN')
  ) {
    // U1.1 fix UX: feedback explícito do bloqueio. Antes redirect silencioso pro hub
    // criava sensação de "cliquei e nada aconteceu".
    const toast = useToast();
    const titulo = String(to.meta['title'] ?? to.name ?? 'rota');
    toast.error(
      `Acesso negado a "${titulo}". Authority necessária: ${requiredAuthority}. ` +
      `Seu papel atual: ${auth.user?.role ?? 'desconhecido'}.`,
      8000,
    );
    console.warn('[router] bloqueio authority', {
      route: to.fullPath,
      requiredAuthority,
      userRole: auth.user?.role,
      userAuthorities: auth.user?.authorities,
    });
    return { name: 'hub' };
  }
  return true;
});
